// AI Generated Component
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { UptimeRepoService } from '@client/app/services/api/uptime-repo/uptime-repo.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { StateService } from '@uirouter/core';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonPromptService } from '@monsido/angular-shared-components';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { UptimeComponent } from '@client/app/forms/uptime/uptime.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { AppClockService } from '@client/app/core/services/app-clock.service';

@Component({
    selector: 'uptimes-overview',
    standalone: false,
    template: `
        <mon-spinner [loading]="loading">
            <page-header
                    icon="fas fa-heartbeat"
                    header="{{'Uptime Monitoring' | translate}}"
                    subHeader="{{'Check whether your website is responding and measures the response times of the server' | translate }}">
                <page-header-content>
                    <div class="row">
                        <div class="col-48 text-right">
                            <button class="btn btn-border-radius btn-secondary"
                                    (click)="onAddNew()"
                                    monEventTrigger="actionTrigger"
                                    [monEventParams]="{action: 'add-heartbeat-check-btn'}"
                                    role="button"
                                    [attr.aria-label]="'Add new check' | translate"
                                    translate>
                                + Add new check
                            </button>
                        </div>
                    </div>
                </page-header-content>
            </page-header>
            <div class="card panel-monsido-card">
                <div class="card-header">
                    <mon-panel-header-next
                            pHeader="{{ 'All heartbeat checkpoints' | translate }}">
                    </mon-panel-header-next>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-lg-48">
                            <table-container
                                    [collection]="uptimes"
                                    (onPageChange)="onPageChange($event)"
                                    (onPerPageChange)="onPageSizeChange($event)"
                                    [progress]="promise">
                                <table class="table table-hover table-fixed">
                                    <thead>
                                    <tr>
                                        <th class="col-sm-14 col-md-16" translate>Heartbeat check</th>
                                        <th class="col-sm-10 col-md-10 text-right" translate>Check site status</th>
                                        <th class="col-sm-12 col-md-10 text-right" translate>Current domain status</th>
                                        <th class="col-sm-6 col-md-6">&nbsp;</th>
                                        <th class="col-sm-6 col-md-6">&nbsp;</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr *ngFor="let uptime of uptimes">
                                        <td class="text-ellipsis text-v-middle">
                                            <a [href]="uptime.url"
                                               target="_blank" aria-describedby="linkOpensInNewTab"
                                               [title]="'Go to page' | translate">
                                                <span class="fas fa-external-link-alt"></span>
                                                <span class="sr-only">{{uptime.url}}</span>
                                            </a>
                                            <span class="pl-1 text-secondary-4">{{uptime.url}}</span>
                                        </td>
                                        <td class="text-v-middle text-right">
                                            <span class="fas fa-circle"
                                                  [ngClass]="{'text-light-green': uptime.status, 'text-light-red': !uptime.status}">
                                            </span>
                                            <span *ngIf="uptime.status" translate>Active</span>
                                            <span *ngIf="!uptime.status" translate>Inactive</span>
                                        </td>
                                        <td class="text-right text-v-middle">
                                            <div class="d-flex justify-content-end">
                                                <div>
                                                    <span class="fas fa-circle"
                                                          [ngClass]="{'text-light-green': uptime.last_result, 'text-light-red': !uptime.last_result}">
                                                    </span>
                                                </div>
                                                <div class="pl-2">
                                                    <span *ngIf="uptime.last_result === true" translate>OK</span>
                                                    <span translate
                                                          class="inline-block"
                                                          *ngIf="uptime.last_result === false"
                                                          [translate-args]="{lastCheck: uptime.last_check_at | amTimeAgo:now}">
                                                        Domain is not responding<br>
                                                        Last check was: [[lastCheck]]
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="text-right text-v-middle text-right"
                                            [attr.aria-label]="'Actions' | translate">
                                            <mon-action-menu [monActionMenuItems]="uptime.dropdown">
                                                <span translate>Action</span>
                                            </mon-action-menu>
                                        </td>
                                        <td class="text-right text-v-middle text-right"
                                            [attr.aria-label]="'See details' | translate">
                                            <a class="btn" (click)="uptimeShow(uptime)"
                                               monEventTrigger="actionTrigger"
                                               [monEventParams]="{action:'heartbeat-details'}">
                                                <span class="fas fa-heartbeat fa-lg outer-r-mini"
                                                      aria-hidden="true"></span>
                                                <span translate>See Details</span>
                                            </a>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </table-container>
                        </div>
                    </div>
                </div>
            </div>
        </mon-spinner>
    `,
})
export class UptimesOverviewComponent extends BaseContainerComponentController implements OnInit {
    loading: boolean = true;
    uptimes: any[] = [];
    date: any = {
        startDate: '',
        endDate: '',
    };
    MAX_CHECKS_RECOMMENDED: number = 10;
    override search: string = 'all';
    sortBy: string = '';
    sortDirection: string = '';
    override activeTab: string = 'active';
    promise: any;
    now = new Date();

    constructor (
        private uptimeRepoService: UptimeRepoService,
        private paramService: ParamService,
        private stateService: StateService,
        private monModalService: MonModalService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
        private translateService: TranslateService,
        private monPromptService: MonPromptService,
        private appClockService: AppClockService,
        private cdRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
    ) {
        super({ table: true });
    }

    ngOnInit (): void {
        this.loading = true;
        this.uptimes = [];
        this.date = {
            startDate: '',
            endDate: '',
        };
        this.MAX_CHECKS_RECOMMENDED = 10;

        this.loadLocations();
        this.getPage();

        const clockIntervalSubscription = this.appClockService.minuteInterval.subscribe(() => {
            this.now = new Date();
            this.cdRef.markForCheck();
        });
        this.destroyRef.onDestroy(() => {
            clockIntervalSubscription.unsubscribe();
        });
    }

    private loadLocations (): void {
        // Access location search parameters
        const search = new URLSearchParams(window.location.search);
        this.search = search.get('search') || 'all';
        this.sortBy = search.get('sort_by') || '';
        this.sortDirection = search.get('sort_dir') || '';
        this.activeTab = search.get('status') || 'active';

        // Set default dates using native Date
        if (!this.date.startDate) {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);
            startDate.setHours(0, 0, 0, 0);
            this.date.startDate = startDate;
        }

        if (!this.date.endDate) {
            const endDate = new Date();
            endDate.setDate(endDate.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            this.date.endDate = endDate;
        }
    }

    override getPage = (): void => {
        this.paramService.setParams({
            page: this.page,
            page_size: this.pageSize,
            sort_by: this.sortBy || null,
            sort_dir: this.sortDirection || null,
            search: this.search || null,
            status: this.activeTab || null,
            start_date: this.date.startDate,
            end_date: this.date.endDate,
        });
        this.getPages();
    };

    private getPages (): void {
        const params: any = {
            page: this.page,
            page_size: this.pageSize,
            status: this.activeTab,
            sort_by: this.sortBy,
            sort_direction: this.sortDirection,
            start_date: this.formatDate(this.date.startDate),
            end_date: this.formatDate(this.date.endDate),
        };

        if (this.activeTab === 'filtered' && this.search) {
            params.search = this.search;
        }

        this.promise = this.uptimeRepoService.getAll(params)
            .then((data: any) => {
                this.uptimes = data;
                this.updateDropdowns();

                if (this.uptimes.length === 1) {
                    this.uptimeShow(this.uptimes[0]);
                } else {
                    this.loading = false;
                }
            })
            .catch(() => {
                // Handle error silently like angular.noop
                this.loading = false;
            });
    }

    private formatDate (date: any): string {
        if (date && date.format) {
            return date.format('YYYY-MM-DD');
        } else if (date instanceof Date) {
            return date.toISOString().split('T')[0];
        }
        return '';
    }

    private updateDropdowns (): void {
        for (const uptime of this.uptimes) {
            const dropdown = [
                {
                    label: this.translateService.getString('Edit'),
                    leftIcon: 'faCog',
                    action: () => this.openUptime(uptime),
                    eventParams: { action: 'heartbeat-edit' },
                },
                {
                    label: this.translateService.getString('Remove checkpoint'),
                    leftIcon: 'faTrashAlt',
                    action: () => this.uptimeDelete(uptime),
                    eventParams: { action: 'heartbeat-remove-check' },
                },
            ];

            uptime.dropdown = dropdown.map(item => {
                return {
                    label: item.label,
                    leftIcon: item.leftIcon,
                    action: () => {
                        this.monEventsService.run('actionTrigger', item.eventParams);
                        item.action();
                    },
                };
            });
        }
    }

    onAddNew (): void {
        if (this.uptimes.length >= this.MAX_CHECKS_RECOMMENDED) {
            this.showPrompt();
        } else {
            this.openUptime();
        }
    }

    openUptime (uptime?: any): void {
        const params = {
            body: UptimeComponent,
            size: 'md',
            data: {
                uptime: uptime,
            },
        };
        const callback = (uptimeResult: any) => {
            if (uptimeResult && typeof uptimeResult === 'object') {
                this.getPage();
            }
        };

        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
    }

    private showPrompt (): void {
        const message = 'Every page added is checked by Heartbeat several times every hour. ' +
            'Each check involves Acquia Web Governance making a request to your website server. ' +
            'Be aware that adding additional pages will increase the total number of requests ' +
            'which may cause issues with server load on your website or increase your hosting costs ' +
            'with your website hosting provider.';
        const title = 'Website performance and hosting cost warning';
        this.monPromptService.custom({
            message: this.translateService.getString(message),
            title: this.translateService.getString(title),
            buttons: [
                {
                    label: this.translateService.getString('Cancel'),
                    className: 'btn btn-default',
                    callback: () => {},
                },
                {
                    label: this.translateService.getString('Proceed to add one more check'),
                    className: 'btn btn-primary',
                    callback: () => this.openUptime(),
                },
            ],
        })
            .catch(() => {});
    }

    uptimeDelete (uptime: any): void {
        const msg = 'Are you sure you want to delete the checkpoint for <strong>' + uptime.url + '</strong>?';
        this.monModalService.confirm(msg).then(() => {
            this.uptimeRepoService.destroy(uptime.id).then(() => {
                this.getPage();
            }, () => {
                // Handle error silently like angular.noop
            });
        }, () => {
            // Handle dismissal silently like angular.noop
        });
    }

    uptimeShow (uptime: any): void {
        const to = 'base.customer.domain.uptimes.show';
        this.monUIRouterTransitionService.onTransitionFinished(
            () => {
                this.stateService.go(to, { uptimeId: uptime.id });
            },
        );
    }
}
